import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService, PayoutFilters } from '../services/api';
import toast from 'react-hot-toast';

export const usePayouts = (params?: PayoutFilters) => {
  return useQuery({
    queryKey: ['payouts', params],
    queryFn: () => apiService.getPayouts(params),
    refetchOnWindowFocus: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const usePayoutStats = () => {
  return useQuery({
    queryKey: ['payoutStats'],
    queryFn: () => apiService.getPayoutStats(),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  });
};

export const usePayoutExportHistory = (limit?: number) => {
  return useQuery({
    queryKey: ['payoutExportHistory', limit],
    queryFn: () => apiService.getPayoutExportHistory(limit),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
};

export const useBulkExportPayouts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payoutIds: string[]) => apiService.bulkExportPayouts(payoutIds),
    onSuccess: (data) => {
      // Download the Excel file
      if (data.excelBuffer) {
        const byteCharacters = atob(data.excelBuffer);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `payouts-export-${data.batchId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success(`Exported ${data.count} payouts (€${data.totalAmount.toFixed(2)})`);
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      queryClient.invalidateQueries({ queryKey: ['payoutStats'] });
      queryClient.invalidateQueries({ queryKey: ['payoutExportHistory'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to export payouts');
    },
  });
};

export const useBulkRejectPayouts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ payoutIds, reason }: { payoutIds: string[]; reason: string }) =>
      apiService.bulkRejectPayouts(payoutIds, reason),
    onSuccess: (data) => {
      toast.success(`Rejected ${data.count} payouts`);
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      queryClient.invalidateQueries({ queryKey: ['payoutStats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to reject payouts');
    },
  });
};

export const useBulkMarkPayoutsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payoutIds: string[]) => apiService.bulkMarkPayoutsPaid(payoutIds),
    onSuccess: (data) => {
      toast.success(`Marked ${data.count} payouts as paid`);
      queryClient.invalidateQueries({ queryKey: ['payouts'] });
      queryClient.invalidateQueries({ queryKey: ['payoutStats'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to mark payouts as paid');
    },
  });
};
